import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { SearchableSelect } from './SearchableSelect';
import { FaEye } from 'react-icons/fa';
import { MdEdit } from 'react-icons/md';

import { useGetProductsQuery, useUpdateProductStatusMutation } from '../../redux/productApi';
import { useGetProductTypesQuery } from '../../redux/productTypeApi';
import { useGetProductCategoriesQuery } from '../../redux/productCategoryApi';
import { useGetProductSubcategoriesQuery } from '../../redux/productSubcategoryApi';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { AuthorizedMedia } from '../AuthorizedMedia';
import { useAuth } from '../../context/AuthContext';
import TablePagination from './TablePagination';
import { ApiLoadState, ApiLoadTableRow } from './ApiLoadState';
import { DEFAULT_PAGE_SIZE, getServerPagination } from '../../utils/pagination';

function AdminProductsList() {
  const { canAction } = useAuth();
  const canView = canAction('products', 'view');
  const canCreate = canAction('products', 'create');
  const canEdit = canAction('products', 'edit');
  const canDelete = canAction('products', 'delete');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProductType, setFilterProductType] = useState('all');
  const pageSize = DEFAULT_PAGE_SIZE;
  const listQuery = useMemo(() => {
    const params = { page, per_page: pageSize };
    const q = search.trim();
    if (q) params.search = q;
    if (filterStatus === 'active') params.status = true;
    else if (filterStatus === 'inactive') params.status = false;
    if (filterProductType && filterProductType !== 'all') {
      params.product_type_id = filterProductType;
    }
    return params;
  }, [page, pageSize, search, filterStatus, filterProductType]);
  const { data: productsRes, isFetching, isLoading, error, refetch } = useGetProductsQuery(listQuery);
  const [imageSlide, setImageSlide] = useState(0);
  const [viewProductDetail, setViewProductDetail] = useState(null);
  const [updateProductStatus, { isLoading: isUpdating }] = useUpdateProductStatusMutation();
  const { data: productTypesRes } = useGetProductTypesQuery({ per_page: 500, status: true });
  const { data: categoriesRes } = useGetProductCategoriesQuery({ per_page: 500 });
  const { data: subcategoriesRes } = useGetProductSubcategoriesQuery({ per_page: 500 });

  const extractImageUrls = (p) => {
    const list = [];
    const rawImages = Array.isArray(p.images) ? p.images : [];

    rawImages.forEach((img) => {
      if (!img) return;
      if (typeof img === 'string') {
        list.push(img);
        return;
      }
      if (img.url) list.push(img.url);
      if (img.image_url) list.push(img.image_url);
    });

    const fallbackSingle =
      p.image_url || p.imageUrl || p.image || p.image_url_path || '';
    if (fallbackSingle) list.push(fallbackSingle);

    const imageUrlsAlt = Array.isArray(p.image_urls) ? p.image_urls : [];
    imageUrlsAlt.forEach((u) => {
      if (u) list.push(u);
    });

    return [...new Set(list.filter(Boolean).map((u) => resolveAssetUrl(u)).filter(Boolean))];
  };

  const products = useMemo(() => {
    const list = productsRes?.data || productsRes || [];
    const safe = Array.isArray(list) ? list : [];
    return safe
      .slice()
      .sort((a, b) => Number(a.id) - Number(b.id))
      .map((p) => {
        const categoryId =
          p.product_category_id ??
          p.productCategoryId ??
          p.category_id ??
          p.categoryId ??
          '';
        const categoryName =
          p.product_category_name ||
          p.productCategoryName ||
          p.product_category?.name ||
          p.category_name ||
          p.categoryName ||
          (categoriesRes?.data || []).find((c) => Number(c.id) === Number(categoryId))
            ?.name ||
          p.category ||
          '';
        const subCategoryId =
          p.product_subcategory_id ??
          p.productSubcategoryId ??
          p.subcategory_id ??
          p.subcategoryId ??
          '';
        const subCategoryName =
          p.product_subcategory_name ||
          p.productSubcategoryName ||
          p.product_subcategory?.name ||
          p.subcategory_name ||
          p.subcategoryName ||
          (subcategoriesRes?.data || []).find((s) => Number(s.id) === Number(subCategoryId))
            ?.name ||
          p.subCategory ||
          '';
        return {
          id: p.id,
          name: p.item_name || p.name || p.itemName || '',
          category: categoryName,
          categoryId,
          subCategory: subCategoryName,
          subCategoryId,
          productType:
            p.product_type_name ||
            p.productTypeName ||
            p.product_type?.name ||
            '',
          productTypeId:
            p.product_type_id ?? p.productTypeId ?? p.product_type?.id ?? '',
          batchNo: p.batch_number || p.batchNo || '',
          mfgDate: p.mfg_date || p.mfgDate || '',
          bestBefore: p.best_before || p.bestBefore || '',
          buffer: p.buffer ?? '',
          leadTime: p.lead_time ?? p.leadTime ?? '',
          projectGrowth: p.project_growth ?? p.projectGrowth ?? '',
          guestCount: p.guest_count ?? p.guestCount ?? '',
          unit: p.unit ?? '',
          itemCategory: p.item_category || p.itemCategory || '',
          sps1: p.sps_1 ?? p.sps1 ?? '',
          sps2: p.sps_2 ?? p.sps2 ?? '',
          sps3: p.sps_3 ?? p.sps3 ?? '',
          itemCode: p.item_code || p.itemCode || '',
          status: p.status,
          imageUrls: extractImageUrls(p),
          _statusRaw: p.status,
        };
      });
  }, [productsRes, categoriesRes, subcategoriesRes]);

  const statusFilterOptions = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  const productTypeFilterOptions = useMemo(() => {
    const list = productTypesRes?.data || productTypesRes || [];
    const types = (Array.isArray(list) ? list : []).map((type) => ({
      value: String(type.id),
      label: type.name || type.product_type_name || String(type.id),
    }));
    return [{ value: 'all', label: 'All' }, ...types];
  }, [productTypesRes]);

  const { currentPage, totalCount, pageSize: effectivePageSize } = getServerPagination(
    productsRes,
    { page, pageSize }
  );
  const pageItems = products;
  const countReady = Boolean(productsRes) && !error;
  const hasActiveFilters =
    Boolean(search.trim()) ||
    filterStatus !== 'all' ||
    (filterProductType && filterProductType !== 'all');
  const countLabel = hasActiveFilters ? 'Matching Products' : 'Total Products';

  const toggleActive = (product) => {
    if (isUpdating) return;
    const currentActive = product.status === true || product.status === 'true' || product.status === 1;
    updateProductStatus({ id: product.id, status: !currentActive })
      .unwrap()
      .then(() => toast.success('Product status updated successfully.'))
      .catch((err) =>
        toast.error(err?.data?.message || 'Failed to update product status.')
      );
  };

  return (
    <DashboardLayout title={"Products List"}>
      <div className="space-y-4 py-4">

        <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex w-full min-w-0 flex-col gap-3 lg:max-w-4xl lg:flex-row lg:items-center">
              <div className="relative w-full min-w-0 md:max-w-xs">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="Search Product"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-gray-200 bg-[#faf8f5] py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                />
              </div>
              <label className="flex w-full min-w-0 flex-col gap-1.5 text-sm text-gray-700 md:flex-row md:items-center md:gap-2 md:w-56 md:shrink-0">
                <span className="shrink-0 font-medium">Filter By:</span>
                <div className="w-full min-w-0">
                  <SearchableSelect
                    name="filterStatus"
                    value={filterStatus}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFilterStatus(v === '' ? 'all' : v);
                      setPage(1);
                    }}
                    placeholder="Status"
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                    options={statusFilterOptions}
                  />
                </div>
              </label>
              <label className="flex w-full min-w-0 flex-col gap-1.5 text-sm text-gray-700 md:flex-row md:items-center md:gap-2 md:w-64 md:shrink-0">
                <span className="shrink-0 font-medium">Product Type:</span>
                <div className="w-full min-w-0">
                  <SearchableSelect
                    name="filterProductType"
                    value={filterProductType}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFilterProductType(v === '' ? 'all' : v);
                      setPage(1);
                    }}
                    placeholder="Select"
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                    options={productTypeFilterOptions}
                  />
                </div>
              </label>
            </div>
            {canCreate ? (
              <Link
                to="/dashboard/admin/products/add"
                className="inline-flex w-full shrink-0 items-center justify-center rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm md:w-auto"
              >
                + Add Product
              </Link>
            ) : null}
          </div>

          <ApiLoadState
            isLoading={isLoading}
            isFetching={isFetching}
            hasData={products.length > 0}
            error={error}
            refetch={refetch}
            message="Loading products…"
            className="py-6 md:hidden"
          />
          {!error && (products.length > 0 || (!isLoading && !isFetching)) && pageItems.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500 md:hidden">No products found.</p>
          ) : !error && (products.length > 0 || (!isLoading && !isFetching)) && pageItems.length > 0 ? (
            <ul className="mb-4 space-y-3 md:hidden">
              {pageItems.map((p, index) => {
                const isActive =
                  p.status === true || p.status === 'true' || p.status === 1;
                return (
                  <li
                    key={p.id}
                    className="rounded-lg border border-gray-100 bg-[#faf8f5] p-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500">
                          #{(currentPage - 1) * pageSize + index + 1}
                        </p>
                        {p.itemCode ? (
                          <p className="mt-1 text-xs font-medium text-gray-700">Code: {p.itemCode}</p>
                        ) : null}
                        <p className="mt-1 text-sm font-semibold text-gray-900">{p.name}</p>
                        <p className="mt-2 text-xs text-gray-600">
                          {p.category}
                          {p.subCategory ? (
                            <>
                              <span className="mx-1 text-gray-300">·</span>
                              {p.subCategory}
                            </>
                          ) : null}
                        </p>
                        <div className="mt-2 space-y-1 text-xs text-gray-600">
                          {p.batchNo ? (
                            <p>
                              Batch:{' '}
                              <span className="font-medium text-gray-800">{p.batchNo}</span>
                            </p>
                          ) : null}
                          {p.mfgDate ? (
                            <p>
                              MFG:{' '}
                              <span className="font-medium text-gray-800">{p.mfgDate}</span>
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {canView ? (
                          <button
                            type="button"
                            onClick={() => {
                              setViewProductDetail(p);
                              setImageSlide(0);
                            }}
                            className="rounded-lg p-1.5 text-red-700 hover:bg-white"
                            aria-label="View product"
                          >
                            <FaEye className="h-5 w-5" />
                          </button>
                        ) : null}
                        {canEdit ? (
                          <Link
                            to={`/dashboard/admin/products/edit/${p.id}`}
                            className="rounded-lg p-1.5 text-red-700 hover:bg-white"
                            aria-label="Edit product"
                          >
                            <MdEdit className="h-5 w-5" />
                          </Link>
                        ) : null}
                      </div>
                    </div>
                    {canDelete ? (
                      <div className="mt-3 flex items-center justify-between border-t border-gray-200/80 pt-3">
                        <span className="text-xs text-gray-500">
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                        <button
                          type="button"
                          disabled={isUpdating}
                          onClick={() => toggleActive(p)}
                          aria-label={isActive ? 'Disable product' : 'Enable product'}
                          className={`inline-flex h-5 w-10 items-center rounded-full border px-[2px] transition disabled:opacity-50 ${
                            isActive
                              ? 'border-emerald-600 bg-[#008E36]'
                              : 'border-gray-300 bg-gray-300'
                          }`}
                        >
                          <span
                            className={`h-4 w-4 rounded-full bg-white shadow transition-transform ${
                              isActive ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="whitespace-nowrap border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                    Sr. No.
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                    Item Code
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                    Item Name
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                    Product Category
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                    Product Sub Category
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-700">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                <ApiLoadTableRow
                  colSpan={8}
                  isLoading={isLoading}
                  isFetching={isFetching}
                  hasData={products.length > 0}
                  error={error}
                  refetch={refetch}
                  message="Loading products..."
                />
                {!error && (products.length > 0 || (!isLoading && !isFetching)) &&
                  pageItems.map((p, index) => (
                  <tr key={p.id} className="hover:bg-amber-50/40">
                    <td className="whitespace-nowrap border-b border-gray-100 px-3 py-2 text-gray-600">
                      {(currentPage - 1) * pageSize + index + 1}
                    </td>
                    <td className="border-b border-gray-100 px-3 py-2">{p.itemCode}</td>
                    <td className="border-b border-gray-100 px-3 py-2">{p.name}</td>
                    <td className="border-b border-gray-100 px-3 py-2">{p.category}</td>
                    <td className="border-b border-gray-100 px-3 py-2">{p.subCategory}</td>
                    <td className="border-b border-gray-100 px-3 py-2">
                      <div className="flex items-center gap-3">
                        {canView ? (
                          <button
                            type="button"
                            onClick={() => {
                              setViewProductDetail(p);
                              setImageSlide(0);
                            }}
                            className="rounded-md p-1 text-red-700 hover:bg-amber-50/60 hover:text-red-800"
                            aria-label="View product"
                            title="View"
                          >
                            <FaEye className="h-5 w-5 text-red-700" />
                          </button>
                        ) : null}
                        {canEdit ? (
                          <Link
                            to={`/dashboard/admin/products/edit/${p.id}`}
                            className="rounded-md p-1 text-red-700 hover:bg-amber-50/60 hover:text-red-800"
                            aria-label="Edit product"
                            title="Edit"
                          >
                            <MdEdit className="h-5 w-5 text-red-700" />
                          </Link>
                        ) : null}
                        {canDelete ? (
                          <button
                            type="button"
                            disabled={isUpdating}
                            onClick={() => toggleActive(p)}
                            aria-label={p.status ? 'Disable product' : 'Enable product'}
                            className={`inline-flex h-5 w-10 items-center rounded-full border px-[2px] transition disabled:opacity-50 ${
                              p.status === true || p.status === "true" || p.status === 1
                                ? 'border-emerald-600 bg-[#008E36]'
                                : 'border-gray-300 bg-gray-300'
                            }`}
                          >
                            <span
                              className={`h-4 w-4 rounded-full bg-white shadow transition-transform ${
                                p.status === true || p.status === "true" || p.status === 1
                                  ? 'translate-x-5'
                                  : 'translate-x-0'
                              }`}
                            />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
                {!error && !isLoading && !isFetching && pageItems.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-4 text-center text-sm text-gray-500">
                      No products found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 grid grid-cols-1 items-center gap-2 sm:grid-cols-3">
            <p
              className="text-center text-sm text-gray-600 sm:text-left"
              aria-label={`${countLabel}: ${countReady ? totalCount : 'loading'}`}
            >
              <span className="font-medium text-gray-500">{countLabel}</span>
              {': '}
              <span className="font-semibold tabular-nums text-brand">
                {countReady ? totalCount.toLocaleString('en-IN') : '—'}
              </span>
            </p>
            <div className="flex justify-center [&>div]:mt-0">
              <TablePagination
                totalCount={totalCount}
                currentPage={currentPage}
                onPageChange={setPage}
                pageSize={effectivePageSize}
              />
            </div>
            <div className="hidden sm:block" aria-hidden />
          </div>
        </div>


        {viewProductDetail && (
          <div
            className="fixed inset-0 z-40 flex justify-end bg-black/30"
            onClick={() => setViewProductDetail(null)}
            role="presentation"
          >
            <div
              className="flex h-full w-full min-w-0 max-w-full flex-col bg-white shadow-2xl sm:max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-4 py-4 sm:px-6">
                <h2 className="text-lg font-semibold text-brand">Product Details</h2>
                <button
                  type="button"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                  onClick={() => setViewProductDetail(null)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 pb-10 sm:px-6 sm:pb-8">
              <div className="relative w-full overflow-hidden rounded-lg bg-gray-100">
                {viewProductDetail.imageUrls?.length > 0 ? (
                  <div className="relative aspect-video w-full">
                    <AuthorizedMedia
                      src={
                        viewProductDetail.imageUrls[
                          imageSlide % viewProductDetail.imageUrls.length
                        ]
                      }
                      alt={`${viewProductDetail.name || 'Product'} ${imageSlide + 1}`}
                      className="h-full w-full object-contain"
                    />
                    {viewProductDetail.imageUrls.length > 1 && (
                      <>
                        <button
                          type="button"
                          className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/60 px-3 py-1 text-white"
                          onClick={() =>
                            setImageSlide(
                              (prev) =>
                                (prev - 1 + viewProductDetail.imageUrls.length) %
                                viewProductDetail.imageUrls.length
                            )
                          }
                          aria-label="Previous image"
                        >
                          ‹
                        </button>
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/60 px-3 py-1 text-white"
                          onClick={() =>
                            setImageSlide((prev) => (prev + 1) % viewProductDetail.imageUrls.length)
                          }
                          aria-label="Next image"
                        >
                          ›
                        </button>
                        <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5 rounded-full bg-black/45 px-2 py-1">
                          {viewProductDetail.imageUrls.map((_, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => setImageSlide(idx)}
                              className={`h-2 w-2 rounded-full ${
                                idx === imageSlide ? 'bg-white' : 'bg-white/50'
                              }`}
                              aria-label={`Go to image ${idx + 1}`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex aspect-video min-h-[160px] w-full items-center justify-center text-xs text-gray-400">
                    No image
                  </div>
                )}
              </div>
              <div className="mt-4 space-y-2 sm:grid sm:grid-cols-2 sm:gap-3 sm:space-y-0">
                {[
                  { label: 'Item Code', value: viewProductDetail.itemCode || '—' },
                  { label: 'Item Name', value: viewProductDetail.name || '—' },
                  { label: 'Product Type', value: viewProductDetail.productType || '—' },
                  { label: 'Product Category', value: viewProductDetail.category || '—' },
                  { label: 'Product Sub Category', value: viewProductDetail.subCategory || '—' },
                  {
                    label: 'Item Category (%)',
                    value:
                      viewProductDetail.itemCategory !== '' &&
                      viewProductDetail.itemCategory != null
                        ? String(viewProductDetail.itemCategory).trim().endsWith('%')
                          ? String(viewProductDetail.itemCategory).trim()
                          : `${viewProductDetail.itemCategory}%`
                        : '—',
                  },
                  { label: 'Unit', value: viewProductDetail.unit || '—' },
                  { label: 'SPS 1', value: viewProductDetail.sps1 !== '' && viewProductDetail.sps1 != null ? viewProductDetail.sps1 : '—' },
                  { label: 'SPS 2', value: viewProductDetail.sps2 !== '' && viewProductDetail.sps2 != null ? viewProductDetail.sps2 : '—' },
                  { label: 'SPS 3', value: viewProductDetail.sps3 !== '' && viewProductDetail.sps3 != null ? viewProductDetail.sps3 : '—' },
                  { label: 'Batch No. (As Per Actual)', value: viewProductDetail.batchNo || '—' },
                  { label: 'MFG Date', value: viewProductDetail.mfgDate || '—' },
                  { label: 'Use Before / Best Before', value: viewProductDetail.bestBefore || '—' },
                  {
                    label: 'Status',
                    value:
                      viewProductDetail.status === true ||
                      viewProductDetail.status === 'true' ||
                      viewProductDetail.status === 1
                        ? 'Active'
                        : 'Inactive',
                  },
                ].map((field) => (
                  <div
                    key={field.label}
                    className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0"
                  >
                    <p className="text-xs text-gray-500">{field.label}</p>
                    <p className="mt-0.5 text-sm font-medium text-gray-900">{field.value}</p>
                  </div>
                ))}
              </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default AdminProductsList;